import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addContractorAttributesFiltered,
  addContractorAttributesWhereEmpty,
  addContractorAttributeToServiceType,
  addGlobalContractorAttributes,
  addToAllServiceContractorAttributes,
  editContractorAttributeValue,
  editFilledContractorAttributes,
  editGlobalContractorAttribute,
  getContractorAttributesPaged,
  getGlobalContractorAttributes,
  getServiceTypeAttributes,
  removeContractorAttribute,
  removeContractorAttributes,
  removeGlobalContractorAttribute,
} from "@/requests/administration/contractors/contractorEditAttributes";
import {
  ContractorAttribute,
  ContractorAttributeResponse,
  ContractorServiceAttributes,
} from "@/models/api/administration/contractors/ContractorAttribute";
import { NewContractorAttribute } from "@/models/api/administration/contractors/NewContractorAttribute";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { AttributeInfo } from "@/models/api/administration/contractors/AttributeInfo";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface AdministrationContractorEditAttributesInterface {
  serviceTypeId: number | null;
  serviceTypeIdAttributeInfo: number | null;
  serviceAttributes: Array<AttributeInfo>;
  globalAttributes: Array<ContractorAttribute>;
  isAddAttributeDialog: boolean;
  isAddAttributesDialog: boolean;
  isEditAttributeDialog: boolean;
  addEditAttributeModel: NewContractorAttribute;
  editAttributeId: string | null;
  contractorAttributeFilters: null | Array<CriterionBase>;
  contractorAttributes: Array<ContractorServiceAttributes>;
  totalCount: number;
  dataTableOptions: DataOptions;
}

export const AdministrationContractorEditAttributesState: AdministrationContractorEditAttributesInterface = {
  serviceTypeId: null,
  serviceTypeIdAttributeInfo: null,
  serviceAttributes: [],
  globalAttributes: [],
  isAddAttributeDialog: false,
  isAddAttributesDialog: false,
  isEditAttributeDialog: false,
  addEditAttributeModel: new NewContractorAttribute(),
  editAttributeId: null,
  contractorAttributeFilters: null,
  contractorAttributes: [],
  totalCount: 0,
  dataTableOptions: new DefaultDataOptions(),
};

const AdministrationContractorEditAttributesStateModule = defineModule({
  state: AdministrationContractorEditAttributesState as AdministrationContractorEditAttributesInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setGlobalAttributes(state, param: Array<ContractorAttribute>) {
      state.globalAttributes = param;
    },
    setIsAddAttributeDialog(state, param: boolean) {
      state.isAddAttributeDialog = param;
    },
    setIsAddAttributesDialog(state, param: boolean) {
      state.isAddAttributesDialog = param;
    },
    setIsEditAttributeDialog(state, param: boolean) {
      state.isEditAttributeDialog = param;
    },
    setAddAttributeModel(state, param: NewContractorAttribute) {
      state.addEditAttributeModel = param;
    },
    setAddEditAttributeModel(state, param: NewContractorAttribute) {
      state.addEditAttributeModel = param;
    },
    setEditAttributeModel(state, param: { model: NewContractorAttribute; attributeId: string | null }) {
      state.addEditAttributeModel = param.model;
      state.editAttributeId = param.attributeId;
      state.serviceTypeId = null;
    },
    setServiceTypeId(state, param: number | null) {
      state.serviceTypeId = param;
    },
    setServiceTypeIdAttributeInfo(state, param: number | null) {
      state.serviceTypeIdAttributeInfo = param;
    },
    setServiceAttributes(state, param: Array<AttributeInfo>) {
      state.serviceAttributes = param;
    },
    setContactorAttributeFilter(state, param: Array<CriterionBase>) {
      state.contractorAttributeFilters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setContractorAttributes(state, param: ContractorAttributeResponse) {
      state.contractorAttributes = param.items;
      state.totalCount = param.totalCount;
    },
  },
  actions: {
    async downloadServiceTypeAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.serviceTypeIdAttributeInfo) {
        const response = await getServiceTypeAttributes(state.serviceTypeIdAttributeInfo);
        commit.setServiceAttributes(response);
      }
    },
    async downloadContractorGlobalAttributes(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const response = await getGlobalContractorAttributes(
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setGlobalAttributes(response);
      }
    },
    async addContractorGlobalAttribute(context) {
      const { rootState, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addGlobalContractorAttributes(
          rootState.administration.contractors.list.selectedContractorId,
          state.addEditAttributeModel
        );
        dispatch.downloadContractorGlobalAttributes();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async addContractorAttributeToServiceType(context) {
      const { rootState, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const currentServiceType = state.contractorAttributes.find((x) => x.serviceTypeId == state.serviceTypeId);
        const currentAttribute = currentServiceType?.attributes?.find(
          (x) => x.attributePrototypeId == state.addEditAttributeModel.attributePrototypeId
        );

        if (currentAttribute) {
          const prototypeLabel = rootState.attributes.prototypes?.find(
            (x) => x.id === currentAttribute.attributePrototypeId
          )?.defaultLabel;
          const confirmModel = new ConfirmModel(
            filterCapitalize(
              i18n.t("attributePrototypeIsAlreadyAddedDoYouWantToContinue", {
                attributeType: prototypeLabel,
                attributeName: currentAttribute.name,
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          );
          const isConfirmed = (await rootDispatch.popup.addConfirm(confirmModel)) as boolean;
          if (!isConfirmed) {
            return;
          }
        }
        await addContractorAttributeToServiceType(
          rootState.administration.contractors.list.selectedContractorId,
          state.serviceTypeId as number,
          state.addEditAttributeModel
        );
      }
      rootDispatch.popup.addSavedOkSnackbar();
      dispatch.downloadContractorAttributes();
    },
    async addToAllServiceContractorAttributes(context, param: NewContractorAttribute) {
      const { rootState, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addToAllServiceContractorAttributes(
          rootState.administration.contractors.list.selectedContractorId,
          param
        );
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadContractorAttributes();
      }
    },
    async editFilledContractorAttributes(context, param: NewContractorAttribute) {
      const { rootState, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editFilledContractorAttributes(rootState.administration.contractors.list.selectedContractorId, param);
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadContractorAttributes();
      }
    },
    async addContractorAttributesWhereEmpty(context, param: NewContractorAttribute) {
      const { rootState, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addContractorAttributesWhereEmpty(rootState.administration.contractors.list.selectedContractorId, param);
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadContractorAttributes();
      }
    },
    async addContractorAttributesFiltered(context, param: NewContractorAttribute) {
      const { rootState, dispatch, rootDispatch, state } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addContractorAttributesFiltered(rootState.administration.contractors.list.selectedContractorId, param, {
          count: state.dataTableOptions.itemsPerPage,
          page: state.dataTableOptions.page - 1,
          criterions: _.cloneDeep(state.contractorAttributeFilters),
          filterName: FILTER_NAMES.ContractorServiceTypesAttributesFilter,
        });
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadContractorAttributes();
      }
    },
    async removeContractorAttributes(context, param: number) {
      const { rootState, dispatch, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await removeContractorAttributes(rootState.administration.contractors.list.selectedContractorId, param);
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadContractorAttributes();
      }
    },
    async removeContractorGlobalAttribute(context, attributeId: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await removeGlobalContractorAttribute(attributeId);
      dispatch.downloadContractorGlobalAttributes();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async editContractorGlobalAttribute(context) {
      const { dispatch, rootDispatch, state } = _moduleActionContext(context);
      await editGlobalContractorAttribute(state.editAttributeId as string, state.addEditAttributeModel);
      dispatch.downloadContractorGlobalAttributes();
      dispatch.downloadContractorAttributes();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async editContractorAttributeValue(context, param: { attributeId: string; newValue: string }) {
      await editContractorAttributeValue(param.attributeId, param.newValue);
    },
    async removeContractorAttribute(context, param: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await removeContractorAttribute(param);
      dispatch.downloadContractorAttributes();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async downloadContractorAttributes(context) {
      const { rootState, state, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const result = await getContractorAttributesPaged(
          {
            count: state.dataTableOptions.itemsPerPage,
            page: state.dataTableOptions.page - 1,
            criterions: _.cloneDeep(state.contractorAttributeFilters),
            filterName: FILTER_NAMES.ContractorServiceTypesAttributesFilter,
          },
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorAttributes(result);
      }
    },
  },
});

export default AdministrationContractorEditAttributesStateModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorEditAttributesStateModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(
    context,
    AdministrationContractorEditAttributesStateModule
  );
